import React, { useEffect, useRef, useState } from "react";
import { CustomInput } from "components/custom-form-input/CustomInputs";
import renderLatex from "./RenderLatex";
import { generateQuestions, getSignedUrl } from "ServerAPI/serverAPI";
import { Button, Modal, Badge, Spinner, Card, Form, Row, Col } from "react-bootstrap";
import QuizImageUpload from "../QuizImageUpload";
import { useCookies } from "react-cookie";
import { Info } from "react-feather";

const BLOOM_TAXONOMY = ["Remember", "Understand", "Apply", "Analyze", "Evaluate", "Create"];

const PreviewImage = ({ fileName, effectiveCourseId, activeSection }) => {
    const [url, setUrl] = useState(null);
    const [cookies] = useCookies(["userId"]);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const fetchSignature = async () => {
            try {
                const payload = {
                    courseId: effectiveCourseId,
                    sectionId: activeSection?.sectionId,
                    fileName,
                    method: "GET",
                    userId: cookies.userId,
                };
                const response = await getSignedUrl(payload);
                if (response.data?.preSignedUrl) {
                    setUrl(response.data.preSignedUrl);
                }
            } catch (err) {
                console.error("Failed to load image preview", err);
            }
        };
        if (fileName && effectiveCourseId && activeSection?.sectionId) {
            if (fileName.startsWith("blob:") || fileName.startsWith("http")) {
                setUrl(fileName);
            } else {
                fetchSignature();
            }
        }
    }, [fileName, effectiveCourseId, activeSection, cookies.userId]);

    if (!url) return <div className="text-muted small mt-2"><Spinner animation="border" size="sm" className="me-2" />Loading image...</div>;

    return (
        <>
            <div className="mt-2" onClick={() => setIsFullscreen(true)} style={{ cursor: "pointer" }}>
                <img src={url} alt={fileName} style={{ maxHeight: "200px", maxWidth: "100%", borderRadius: "8px", objectFit: "contain", border: "1px solid #dee2e6", transition: "transform 0.2s" }} onMouseOver={(e) => e.currentTarget.style.transform = "scale(1.02)"} onMouseOut={(e) => e.currentTarget.style.transform = "scale(1)"} />
            </div>

            <Modal show={isFullscreen} onHide={() => setIsFullscreen(false)} fullscreen>
                <Modal.Header closeButton>
                    <Modal.Title className="text-truncate">{fileName}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="d-flex justify-content-center align-items-center bg-dark p-0">
                    <img src={url} alt={fileName} style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }} />
                </Modal.Body>
            </Modal>
        </>
    );
};


const questions = [
    {
        id: 1,
        type: "mcq",
        difficulty: "medium",
        question: "What is considered a billing provider according to the content?",
        explanation:
            "According to the content, a billing provider is 'A healthcare provider who may be an individual, facility, institution, corporate entity, or other organization that supplies health services or items to the patient and submit the medical bills to the insurance company.'",
        source: "data/RCM Training Document.pdf - Billing Providers",
        answers: [
            {
                answer: "A facility that provides healthcare services",
                value: 1,
            },
            {
                answer: "A healthcare provider who supplies services and submits bills",
                value: 2,
            },
            { answer: "A patient who receives healthcare services", value: 3 },
            { answer: "A hospital that bills insurance", value: 4 },
        ],
        correctAnswer: 2,
    },
    {
        id: 2,
        type: "mcq",
        difficulty: "medium",
        question:
            "How is a middle name typically represented in the American name format?",
        explanation:
            "According to the content, the American name format allows for a middle name to be represented with an initial.",
        source: "data/RCM Training Document.pdf - American Name Format",
        answers: [
            { answer: "With a full spelling", value: 1 },
            { answer: "With an initial", value: 2 },
            { answer: "With no representation", value: 3 },
            { answer: "With a nickname", value: 4 },
        ],
        correctAnswer: 2,
    },
    {
        id: 3,
        type: "true_false",
        difficulty: "medium",
        question:
            "Automated or manual transcription is considered medical transcription.",
        explanation:
            "According to the content, medical transcription 'is an associated health profession, which deals in the process of transcription or converting voice-recorded reports as dictated by physicians or other healthcare professionals, into text format.'",
        source: "data/RCM Training Document.pdf - Physician-Patient Encounter",
        answers: ["True", "False"],
        correctAnswer: true,
    },
    {
        id: 4,
        type: "mcq",
        difficulty: "medium",
        question: "Who is a SCP?",
        explanation:
            "A Specialty Care Provider (SCP) is a type of provider the content, with a specific area of expertise.",
        source: "data/RCM Training Document.pdf - Types of Providers",
        answers: [
            { answer: "Primary Care Physician", value: 1 },
            { answer: "Specialty Care Provider", value: 2 },
            { answer: "Cardiologist", value: 3 },
            { answer: "Pediatrician", value: 4 },
        ],
        correctAnswer: 2,
    },
    {
        id: 5,
        type: "fill_blanks",
        difficulty: "medium",
        question: "Martin Luther King Jr. was an __________ minister and activist.",
        explanation:
            "Martin Luther King Jr. is described as an 'American Baptist minister and activist' in the content.",
        source:
            "data/RCM Training Document.pdf - Birthday of Martin Luther King, Jr.",
        answers: [
            { answer: "American Jewish", value: 1 },
            { answer: "American Catholic", value: 2 },
            { answer: "Kenyan Muslim", value: 3 },
            { answer: "American Baptist", value: 4 },
        ],
        answer: "American Baptist",
        correctAnswer: 4,
    },
    {
        id: 6,
        type: "mcq",
        difficulty: "medium",
        question: "What is the purpose of a Taxonomy code?",
        explanation:
            "Taxonomy code is used for the 'identification of healthcare providers and services' according to the content.",
        source: "data/RCM Training Document.pdf - TAXONOMY CODE",
        answers: [
            { answer: "Payment processing", value: 1 },
            { answer: "Insurance registration", value: 2 },
            { answer: "Provider identification", value: 3 },
            { answer: "None of the above", value: 4 },
        ],
        correctAnswer: 3,
    },
    {
        id: 7,
        type: "mcq",
        difficulty: "medium",
        question: "What type of payer is Federal?",
        explanation: "Federal is considered a type of payer in the content.",
        source: "data/RCM Training Document.pdf - Types of Payers",
        answers: [
            { answer: "Commercial", value: 1 },
            { answer: "Managed Care Organization", value: 2 },
            { answer: "Federal", value: 3 },
            { answer: "Liability", value: 4 },
        ],
        correctAnswer: 3,
    },
];



export const DisplayGeneratedQnA = ({
    handleGeneratedQuizSubmit,
    passingPerctage,
    responseData,
    finalAssessment,
    isBulkQuizUpload = false,
    quizTimer = 0,
    effectiveCourseId,
    activeSection,
    learningOutcomes = [],
}) => {
    // const [responseData, setResponseData] = useState(questions);
    const containerRefs = useRef({});
    // Add a ref to keep track of the last temporary ID
    const lastTempIdRef = useRef(0);

    // Function to generate temporary question ID
    const generateTempQuestionId = () => {
        lastTempIdRef.current += 1;
        return `temp_${lastTempIdRef.current}`;
    };

    const [editableData, setEditableData] = useState(() => {
        const groupedData = {};
        responseData.forEach((question) => {
            const key = `${question.type}_${question.difficulty}`;
            if (!groupedData[key]) {
                groupedData[key] = [];
            }
            // Add temporary question_id if it doesn't exist
            const questionWithId = {
                ...question,
                id: question.id || generateTempQuestionId(),
            };
            groupedData[key].push(questionWithId);
        });
        return groupedData;
    });

    useEffect(() => {
        const groupedData = {};
        responseData.forEach((question) => {
            const key = `${question.type}_${question.difficulty}`;
            if (!groupedData[key]) {
                groupedData[key] = [];
            }
            // Add temporary question_id if it doesn't exist
            const questionWithId = {
                ...question,
                id: question.id || generateTempQuestionId(),
            };
            groupedData[key].push(questionWithId);
        });
        return setEditableData(groupedData);
    }, [responseData]);

    const [editingQuestion, setEditingQuestion] = useState(null);
    const [originalQuestion, setOriginalQuestion] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const uploadImageRef = useRef(null);
    const [isUploadingImages, setIsUploadingImages] = useState(false);
    const [uploadModalState, setUploadModalState] = useState({
        isOpen: false,
        sectionKey: null,
        targetId: null,
        targetType: null,
        optionIndex: null,
        existingImages: [],
    });
    const [hasPendingUploads, setHasPendingUploads] = useState(false);

    const handleOpenUploadModal = (sectionKey, questionId, targetType, optionIndex = null, existingImages = []) => {
        setUploadModalState({
            isOpen: true,
            sectionKey,
            targetId: questionId,
            targetType,
            optionIndex,
            existingImages: existingImages || [],
        });
        setHasPendingUploads(false);
    };

    const handleCloseUploadModal = () => {
        setUploadModalState({
            isOpen: false,
            sectionKey: null,
            targetId: null,
            targetType: null,
            optionIndex: null,
            existingImages: [],
        });
    };

    const handleSaveImages = async () => {
        if (uploadImageRef.current) {
            setIsUploadingImages(true);
            try {
                const uploadedImages = await uploadImageRef.current.uploadImages();
                const urls = uploadedImages.map(img => img.url?.split("?")[0].split("/").pop() || img.file?.name).filter(Boolean);

                setEditableData((prevData) => ({
                    ...prevData,
                    [uploadModalState.sectionKey]: prevData[uploadModalState.sectionKey].map((q) => {
                        if (q.id === uploadModalState.targetId) {
                            if (uploadModalState.targetType === "question") {
                                return { ...q, imageQuestion: urls };
                            } else if (uploadModalState.targetType === "option" && q.answers) {
                                const updatedAnswers = [...q.answers];
                                updatedAnswers[uploadModalState.optionIndex] = {
                                    ...updatedAnswers[uploadModalState.optionIndex],
                                    imageOptionName: urls
                                };
                                return { ...q, answers: updatedAnswers };
                            }
                        }
                        return q;
                    }),
                }));
                // Do not close the modal here to allow users to view the 'Image Preview' section
            } catch (error) {
                console.error("Failed to upload images", error);
            } finally {
                setIsUploadingImages(false);
            }
        }
    };

    const handleImageSync = (images) => {
        if (!uploadModalState.isOpen || !uploadModalState.targetId) return;

        const urls = images.map(img => img.url?.split("?")[0].split("/").pop() || img.file?.name).filter(Boolean);

        setEditableData((prevData) => {
            const currentSectionData = prevData[uploadModalState.sectionKey];
            if (!currentSectionData) return prevData;

            return {
                ...prevData,
                [uploadModalState.sectionKey]: currentSectionData.map((q) => {
                    if (q.id === uploadModalState.targetId) {
                        if (uploadModalState.targetType === "question") {
                            return { ...q, imageQuestion: urls };
                        } else if (uploadModalState.targetType === "option" && q.answers) {
                            const updatedAnswers = [...q.answers];
                            updatedAnswers[uploadModalState.optionIndex] = {
                                ...updatedAnswers[uploadModalState.optionIndex],
                                imageOptionName: urls
                            };
                            return { ...q, answers: updatedAnswers };
                        }
                    }
                    return q;
                }),
            };
        });
    };

    const handleEditClick = (question) => {
        // If already editing a different question, revert changes before switching
        if (editingQuestion && editingQuestion !== question) {
            handleCancelClick();
        }

        const sectionKey = `${question.type}_${question.difficulty}`;
        const questionToEdit = editableData[sectionKey].find(
            (q) => q.id === question.id
        );

        setEditingQuestion(questionToEdit);
        setOriginalQuestion({ ...questionToEdit });
    };

    const validateQuestion = (question) => {
        if (!question.question || question.question.trim() === "") {
            return { isValid: false, message: "Question text is required" };
        }

        if (
            !question.correctAnswer ||
            (typeof question.correctAnswer === "string" &&
                question.correctAnswer.trim() === "")
        ) {
            return { isValid: false, message: "Answer is required" };
        }

        if (question.answers && Array.isArray(question.answers)) {
            const emptyOptions = question.answers.some(
                (opt) => !opt.answer || opt.answer.trim() === ""
            );
            if (emptyOptions) {
                return {
                    isValid: false,
                    message: "All options must be filled out",
                };
            }
        }

        return { isValid: true };
    };

    const handleSaveClick = () => {
        if (!editingQuestion) return;

        const sectionKey = `${editingQuestion.type}_${editingQuestion.difficulty}`;
        const currentQuestion = editableData[sectionKey].find(
            (q) => q.id === editingQuestion.id
        );

        const validation = validateQuestion(currentQuestion);
        if (!validation.isValid) {
            alert(validation.message);
            return;
        }

        setHasUnsavedChanges(true);
        setEditingQuestion(null);
        setOriginalQuestion(null);
    };

    const handleCancelClick = () => {
        if (editingQuestion && originalQuestion) {
            const sectionKey = `${editingQuestion.type}_${editingQuestion.difficulty}`;

            setEditableData((prevData) => ({
                ...prevData,
                [sectionKey]: prevData[sectionKey].map((q) =>
                    q.id === editingQuestion.id ? { ...originalQuestion } : q
                ),
            }));
        }
        setEditingQuestion(null);
        setOriginalQuestion(null);
    };

    const handleInputChange = (field, value) => {
        console.log(field, value, typeof value);

        if (!editingQuestion) return;

        const sectionKey = `${editingQuestion.type}_${editingQuestion.difficulty}`;

        setEditableData((prevData) => ({
            ...prevData,
            [sectionKey]: prevData[sectionKey].map((q) =>
                q.id === editingQuestion.id ? { ...q, [field]: value } : q
            ),
        }));
    };

    const setRubricField = (idx, field, value) => {
        if (!editingQuestion) return;
        const sectionKey = `${editingQuestion.type}_${editingQuestion.difficulty}`;

        setEditableData((prevData) => ({
            ...prevData,
            [sectionKey]: prevData[sectionKey].map((q) =>
                q.id === editingQuestion.id
                    ? {
                        ...q,
                        rubric: (q.rubric || []).map((r, i) =>
                            i === idx
                                ? { ...r, [field]: field === "marks" ? Number(value) : value }
                                : r
                        ),
                    }
                    : q
            ),
        }));
    };

    const addRubricCriterion = () => {
        if (!editingQuestion) return;
        const sectionKey = `${editingQuestion.type}_${editingQuestion.difficulty}`;

        setEditableData((prevData) => ({
            ...prevData,
            [sectionKey]: prevData[sectionKey].map((q) =>
                q.id === editingQuestion.id
                    ? {
                        ...q,
                        rubric: [
                            ...(q.rubric || []),
                            { criterion: "", description: "", marks: 0, partial_credit_guidelines: "" },
                        ],
                    }
                    : q
            ),
        }));
    };

    const removeRubricCriterion = (idx) => {
        if (!editingQuestion) return;
        const sectionKey = `${editingQuestion.type}_${editingQuestion.difficulty}`;

        setEditableData((prevData) => ({
            ...prevData,
            [sectionKey]: prevData[sectionKey].map((q) =>
                q.id === editingQuestion.id
                    ? { ...q, rubric: (q.rubric || []).filter((_, i) => i !== idx) }
                    : q
            ),
        }));
    };

    const handleUpdateOptions = (options, optionToUpdate, e) => {
        const updatedOptions = options?.map((opt) => {
            return opt.value === optionToUpdate.value
                ? {
                    ...opt,
                    answer: e.target.value,
                }
                : opt;
        });
        handleInputChange(
            "answers",

            updatedOptions
        );
    };

    const renderQuestionGroup = (sectionKey, globalStartIndex) => {
        const questions = editableData[sectionKey];

        const capitalizeFirst = (str) =>
            str === "null" || !str ? "" : str.charAt(0).toUpperCase() + str.slice(1);

        const firstQ = questions && questions.length > 0 ? questions[0] : {};
        const typePart = firstQ.type || sectionKey.split("_")[0];

        let title = "";
        if (typePart === "mcq") {
            title = "MCQ";
        } else if (typePart === "true_false") {
            title = "True/False";
        } else if (typePart === "fill_in_the_blanks") {
            title = "Fill in the Blanks";
        } else if (typePart === "very_short_answer") {
            title = "Very Short Answer";
        } else if (typePart === "short_answer") {
            title = "Short Answer";
        } else if (typePart === "long_answer") {
            title = "Long Answer";
        } else {
            title = typePart.split('_').map(capitalizeFirst).join(' ');
        }
        title += " Questions";

        return (
            <div key={sectionKey} className="mb-5">
                <div className="d-flex align-items-center gap-3 mb-4">
                    <div className="h4 mb-0 fw-bold text-primary border-start border-4 border-primary ps-3">
                        {title}
                    </div>
                    <Badge bg="light" text="dark" className="border shadow-sm">
                        {questions.length} Items
                    </Badge>
                </div>

                <div className="d-flex flex-column gap-4">
                    {(questions || []).map((q, index) => {
                        const isEditing = editingQuestion?.id === q.id;

                        return (
                            <Card key={q.id} className={`mb-3 ${isEditing ? 'border-primary' : 'border-light'}`}>
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-start gap-3">
                                        <div className="flex-grow-1">
                                            {/* 1. Header Metadata */}
                                            <div className="d-flex flex-wrap gap-2 mb-3">
                                                <Badge bg="light" text="primary" className="border px-2 py-1">
                                                    {q.taxonomy || "Remember"}
                                                </Badge>
                                                <Badge bg="light" text="secondary" className="border px-2 py-1">
                                                    Marks: {q.marks || 1}
                                                </Badge>
                                                {q.topics?.map((topic, tid) => (
                                                    <Badge key={tid} bg="light" text="info" className="border px-2 py-1">
                                                        # {topic}
                                                    </Badge>
                                                ))}
                                            </div>

                                            {/* 2. Question Text */}
                                            {isEditing ? (
                                                <div className="mb-3">
                                                    <CustomInput
                                                        type="textarea"
                                                        id={`q-text-${q.id}`}
                                                        label="Question Text"
                                                        value={q.question}
                                                        onChange={(e) => handleInputChange("question", e.target.value)}
                                                        placeholder="Enter question text"
                                                        maxChars={1000}
                                                        rows={3}
                                                    />
                                                </div>
                                            ) : (
                                                <h5 className="fw-bold mb-3 lh-base">
                                                    <span className="text-primary me-2">Q{globalStartIndex + index + 1}.</span>
                                                    <span dangerouslySetInnerHTML={{ __html: renderLatex(q.question) }} />
                                                </h5>
                                            )}

                                            {/* 3. Options (for MCQ/TF) */}
                                            {(q.type === 'mcq' || q.type === 'true_false') && (
                                                <div className="ms-4 mb-4">
                                                    {isEditing ? (
                                                        <div className="d-flex flex-column gap-3">
                                                            {(q.type === "true_false" ? ["True", "False"] : (q.answers || [])).map((opt, idx) => {
                                                                const optAnswer = typeof opt === 'object' ? opt.answer : opt;
                                                                const optValue = typeof opt === 'object' ? opt.value : (q.type === "true_false" ? (idx === 0 ? "True" : "False") : (idx + 1));
                                                                
                                                                return (
                                                                    <div key={idx} className="d-flex align-items-center gap-3">
                                                                        <Badge bg="light" text="dark" className="border px-2 py-2">
                                                                            {typeof optValue === 'number' ? String.fromCharCode(96 + optValue).toUpperCase() : optValue}
                                                                        </Badge>
                                                                        <div className="flex-grow-1">
                                                                            <Form.Control
                                                                                value={optAnswer || ""}
                                                                                onChange={(e) => {
                                                                                    const newAnswers = [...(q.answers || [])];
                                                                                    if (typeof opt === 'object') {
                                                                                        newAnswers[idx] = { ...opt, answer: e.target.value };
                                                                                    } else {
                                                                                        newAnswers[idx] = e.target.value;
                                                                                    }
                                                                                    handleInputChange('answers', newAnswers);
                                                                                }}
                                                                                placeholder={`Option ${idx + 1}`}
                                                                            />
                                                                        </div>
                                                                        <Form.Check
                                                                            type="switch"
                                                                            checked={String(q.correctAnswer) === String(optValue) || q.correctAnswer === optAnswer || (q.type === "true_false" && ((q.correctAnswer === true && idx === 0) || (q.correctAnswer === false && idx === 1)))}
                                                                            onChange={() => handleInputChange('correctAnswer', optValue)}
                                                                        />
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        q.type === "true_false" ? (
                                                            <div className="d-flex gap-3">
                                                                {["True", "False"].map((optText, idx) => {
                                                                    const isCorrect = String(q.correctAnswer).toLowerCase() === optText.toLowerCase() || 
                                                                                      (q.correctAnswer === true && optText === "True") ||
                                                                                      (q.correctAnswer === false && optText === "False") ||
                                                                                      (String(q.correctAnswer) === "1" && idx === 0) ||
                                                                                      (String(q.correctAnswer) === "2" && idx === 1) ||
                                                                                      (String(q.correctAnswer) === "0" && idx === 0);
                                                                    return (
                                                                        <div key={idx} className={`p-2 rounded border-start border-3 flex-grow-1 ${isCorrect ? 'border-success' : 'bg-light border-transparent'}`} style={isCorrect ? { backgroundColor: 'rgba(25, 135, 84, 0.1)' } : {}}>
                                                                            <span className={isCorrect ? 'text-success fw-bold' : 'text-muted'}>{optText}</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <div className="row g-3">
                                                                {(q.answers || []).map((opt, idx) => {
                                                                    const optAnswer = typeof opt === 'object' ? opt.answer : opt;
                                                                    const optValue = typeof opt === 'object' ? opt.value : (idx + 1);
                                                                    const isCorrect = String(q.correctAnswer) === String(optValue) || String(q.correctAnswer) === String(optAnswer);
                                                                    
                                                                    return (
                                                                        <div key={idx} className="col-md-6">
                                                                            <div className={`p-2 rounded border-start border-3 h-100 ${isCorrect ? 'border-success' : 'bg-light border-transparent'}`} style={isCorrect ? { backgroundColor: 'rgba(25, 135, 84, 0.1)' } : {}}>
                                                                                <div className="d-flex align-items-center gap-2">
                                                                                    <Badge bg="white" text="dark" className="border px-2 py-1">
                                                                                        {typeof optValue === 'number' ? String.fromCharCode(96 + optValue).toUpperCase() : optValue}
                                                                                    </Badge>
                                                                                    <span className={isCorrect ? 'text-success fw-bold' : 'text-muted'}>
                                                                                        {optAnswer}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            )}

                                            {/* 4. Correct Answer & Explanation */}
                                            <div className="bg-light bg-opacity-50 p-3 rounded border border-dashed border-secondary border-opacity-25 mt-3">
                                                <div className="mb-3">
                                                    <h6 className="small fw-bold text-uppercase text-muted mb-2">Correct Answer</h6>
                                                    {isEditing ? (
                                                        <CustomInput
                                                            type="textarea"
                                                            id={`q-ans-${q.id}`}
                                                            label=""
                                                            value={q.correctAnswer}
                                                            onChange={(e) => handleInputChange("correctAnswer", e.target.value)}
                                                            placeholder="Enter correct answer"
                                                            rows={2}
                                                        />
                                                    ) : (
                                                        <div className="fw-bold text-dark" dangerouslySetInnerHTML={{ __html: renderLatex(q.correctAnswer) }} />
                                                    )}
                                                </div>

                                                <div className="mb-3">
                                                    <h6 className="small fw-bold text-uppercase text-muted mb-2">Explanation</h6>
                                                    {isEditing ? (
                                                        <CustomInput
                                                            type="textarea"
                                                            id={`q-expl-${q.id}`}
                                                            label=""
                                                            value={q.explanation}
                                                            onChange={(e) => handleInputChange("explanation", e.target.value)}
                                                            placeholder="Provide detailed explanation"
                                                            maxChars={2000}
                                                            rows={3}
                                                        />
                                                    ) : (
                                                        <div className="text-muted small lh-lg" dangerouslySetInnerHTML={{ __html: renderLatex(q.explanation) }} />
                                                    )}
                                                </div>

                                                {q.courseOutcome && !isEditing && (
                                                    <div className="mt-3 pt-3 border-top border-secondary border-opacity-10">
                                                        <h6 className="small fw-bold text-uppercase text-muted mb-2">Course Outcome</h6>
                                                        <div className="d-flex align-items-center gap-2 p-2 bg-light rounded border border-primary border-opacity-10">
                                                            <Info size={14} className="text-primary flex-shrink-0" />
                                                            <span className="text-dark small fw-medium">
                                                                {q.courseOutcome}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}

                                                {!isEditing && Array.isArray(q.rubric) && q.rubric.length > 0 && (
                                                    <div className="mt-3 pt-3 border-top border-secondary border-opacity-10">
                                                        <h6 className="small fw-bold text-uppercase text-muted mb-2">Rubric</h6>
                                                        <div className="table-responsive">
                                                            <table className="table table-sm table-bordered mb-0 bg-white" style={{ fontSize: '0.85em' }}>
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th>Criterion</th>
                                                                        <th>Description</th>
                                                                        <th style={{ width: '70px' }}>Marks</th>
                                                                        <th>Partial Credit Guidelines</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {q.rubric.map((r, ridx) => (
                                                                        <tr key={ridx}>
                                                                            <td className="fw-semibold">{r.criterion}</td>
                                                                            <td>{r.description}</td>
                                                                            <td>{r.marks}</td>
                                                                            <td>{r.partial_credit_guidelines}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* 5. Additional Metadata (Edit Only) */}
                                            {isEditing && (
                                                <>
                                                    <Row className="g-3 mt-3 border-top pt-3">
                                                        <Col md={12}>
                                                            <Form.Group>
                                                                <Form.Label className="small fw-bold">Marks</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    value={q.marks}
                                                                    onChange={(e) => handleInputChange("marks", parseInt(e.target.value) || 0)}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>

                                                    {Array.isArray(q.rubric) && (
                                                        <div className="border-top pt-3 mt-3">
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <h6 className="small fw-bold text-uppercase text-muted mb-0">Rubric</h6>
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={addRubricCriterion}
                                                                >
                                                                    + Add Criterion
                                                                </Button>
                                                            </div>

                                                            {q.rubric.length === 0 && (
                                                                <div className="text-muted small mb-2">No rubric criteria yet.</div>
                                                            )}

                                                            {q.rubric.map((r, ridx) => (
                                                                <div key={ridx} className="border rounded-3 p-3 mb-2 bg-light">
                                                                    <div className="d-flex justify-content-between align-items-start gap-2">
                                                                        <div className="flex-grow-1">
                                                                            <Row className="g-3">
                                                                                <Col md={9}>
                                                                                    <CustomInput
                                                                                        label="Criterion"
                                                                                        value={r.criterion || ""}
                                                                                        onChange={(e) => setRubricField(ridx, "criterion", e.target.value)}
                                                                                        placeholder="Criterion name"
                                                                                    />
                                                                                </Col>
                                                                                <Col md={3}>
                                                                                    <CustomInput
                                                                                        type="number"
                                                                                        label="Marks"
                                                                                        value={r.marks ?? 0}
                                                                                        onChange={(e) => setRubricField(ridx, "marks", e.target.value)}
                                                                                        placeholder="Marks"
                                                                                    />
                                                                                </Col>
                                                                            </Row>
                                                                            <CustomInput
                                                                                label="Description"
                                                                                value={r.description || ""}
                                                                                onChange={(e) => setRubricField(ridx, "description", e.target.value)}
                                                                                placeholder="Describe what this criterion evaluates"
                                                                                type="textarea"
                                                                                rows={2}
                                                                            />
                                                                            <CustomInput
                                                                                label="Partial Credit Guidelines"
                                                                                value={r.partial_credit_guidelines || ""}
                                                                                onChange={(e) => setRubricField(ridx, "partial_credit_guidelines", e.target.value)}
                                                                                placeholder="Explain how partial credit should be awarded"
                                                                                type="textarea"
                                                                                rows={2}
                                                                            />
                                                                        </div>
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            onClick={() => removeRubricCriterion(ridx)}
                                                                        >
                                                                            <i className="bi bi-trash"></i>
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {/* 6. Action Buttons */}
                                        <div className="d-flex flex-column gap-2">
                                            {isEditing ? (
                                                <>
                                                    <Button variant="success" size="sm" className="shadow-sm" onClick={handleSaveClick}>
                                                        <i className="bi bi-check-lg"></i>
                                                    </Button>
                                                    <Button variant="outline-danger" size="sm" className="shadow-sm" onClick={handleCancelClick}>
                                                        <i className="bi bi-x"></i>
                                                    </Button>
                                                </>
                                            ) : (
                                                <Button variant="outline-primary" size="sm" className="shadow-sm" onClick={() => handleEditClick(q)}>
                                                    <i className="bi bi-pencil-square"></i>
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Render method with grouped questions
    const renderQuestions = () => {
        let globalCounter = 0;
        return Object.keys(editableData).map((sectionKey) => {
            const sectionStartIndex = globalCounter;
            globalCounter += editableData[sectionKey]?.length || 0;
            return renderQuestionGroup(sectionKey, sectionStartIndex);
        });
    };

    return (
        <div>
            <div>
                {renderQuestions()}
                <Button
                    onClick={() =>
                        handleGeneratedQuizSubmit(
                            editableData,
                            Number(passingPerctage),
                            finalAssessment,
                            quizTimer
                        )
                    }
                >
                    Next
                </Button>
            </div>

            <Modal show={uploadModalState.isOpen} onHide={handleCloseUploadModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Upload Images for {uploadModalState.targetType === "question" ? "Question" : `Option ${uploadModalState.optionIndex + 1}`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {uploadModalState.isOpen && (
                        <QuizImageUpload
                            ref={uploadImageRef}
                            onImageUpload={handleImageSync}
                            onPendingFilesChange={setHasPendingUploads}
                            existingImages={uploadModalState.existingImages}
                            effectiveCourseId={effectiveCourseId}
                            activeSection={activeSection}
                            editQuiz={true}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseUploadModal} disabled={isUploadingImages}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleSaveImages} disabled={isUploadingImages || !hasPendingUploads}>
                        {isUploadingImages ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                Uploading...
                            </>
                        ) : "Upload"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

